
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import AdminBasicInfoSection from '@/components/admin/form/AdminBasicInfoSection';
import AdminContactSection from '@/components/admin/form/AdminContactSection';
import AdminProfileSection from '@/components/admin/form/AdminProfileSection';
import AdminDocumentsSection from '@/components/admin/form/AdminDocumentsSection';
import AdminQualificationsSection from '@/components/admin/form/AdminQualificationsSection';
import AdminPermissionsSection from '@/components/admin/form/AdminPermissionsSection';
import { getAdminById, addAdminHistory } from '@/services/db/admins';
import { useAdminSave } from '@/hooks/useAdminSave';
import { Admin, AdminFormData } from '@/types/admin.types';
import { toast } from 'sonner';

const adminFormSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().min(1, { message: 'First name is required.' }),
  lastName: z.string().min(1, { message: 'Last name is required.' }),
  position: z.string().min(1, { message: 'Position is required.' }),
  email: z.string().email({ message: 'Please enter a valid email.' }),
  phone: z.string().min(1, { message: 'Phone number is required.' }),
  country: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
  joinDate: z.string(),
  status: z.enum(['Active', 'Inactive']),
  role: z.enum(['Admin', 'Super Admin', 'Manager']),
  permissions: z.array(z.string()).optional(),
  qualifications: z.array(z.any()).optional(),
  documents: z.array(z.any()).optional(),
  profileImage: z.string().optional(),
  history: z.array(z.any()).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  lastModified: z.string().optional(),
  syncStatus: z.enum(['synced', 'pending', 'error']).optional()
});

type AdminFormValues = z.infer<typeof adminFormSchema>;

const AdminForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [initializing, setInitializing] = useState<boolean>(true);
  const [profilePreview, setProfilePreview] = useState<string>('/placeholder.svg');
  const [selectedTab, setSelectedTab] = useState<string>('basic');
  const [submitError, setSubmitError] = useState<string>('');
  
  const { saveAdmin, isSaving } = useAdminSave();
  
  const form = useForm<AdminFormValues>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      country: '',
      city: '',
      address: '',
      joinDate: new Date().toISOString().split('T')[0],
      status: 'Active',
      role: 'Admin',
      permissions: [],
      qualifications: [],
      documents: [],
      profileImage: '',
      history: [],
      syncStatus: 'synced'
    },
    mode: 'onChange'
  });

  useEffect(() => {
    const loadAdminData = async () => {
      if (id) {
        try {
          console.log(`Loading admin data for ID: ${id}`);
          const admin = await getAdminById(id);
          if (admin) {
            console.log('Admin found, populating form...');
            form.reset({
              id: admin.id,
              firstName: admin.firstName,
              lastName: admin.lastName,
              position: admin.position,
              email: admin.email || '',
              phone: admin.phone || '',
              country: admin.country || '',
              city: admin.city || '',
              address: admin.address || '',
              joinDate: admin.joinDate.split('T')[0],
              status: admin.status,
              role: admin.role as 'Admin' | 'Super Admin' | 'Manager',
              permissions: admin.permissions || [],
              qualifications: admin.qualifications || [],
              documents: admin.documents || [],
              profileImage: admin.profileImage || '',
              history: admin.history || [],
              createdAt: admin.createdAt,
              updatedAt: admin.updatedAt,
              lastModified: admin.lastModified,
              syncStatus: admin.syncStatus || 'synced'
            });
            setProfilePreview(admin.profileImage || '/placeholder.svg');
          } else {
            toast.error('Admin not found');
            navigate('/admins');
          }
        } catch (error) {
          console.error('Error loading admin:', error);
          toast.error('Failed to load admin data');
          navigate('/admins');
        }
      }
      setInitializing(false);
    };

    loadAdminData();
  }, [id, form, navigate]);

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfilePreview(result);
        form.setValue('profileImage', result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: AdminFormValues) => {
    try {
      setSubmitError('');
      console.log('Submitting admin form with data:', data);
      
      const now = new Date().toISOString();
      const adminToSave: Admin = {
        id: data.id || '', // Will be assigned by the service if empty
        firstName: data.firstName,
        lastName: data.lastName,
        position: data.position,
        email: data.email,
        phone: data.phone,
        country: data.country || '',
        city: data.city || '',
        address: data.address || '',
        joinDate: data.joinDate,
        status: data.status,
        role: data.role,
        permissions: data.permissions || [],
        qualifications: data.qualifications || [],
        documents: data.documents || [],
        profileImage: data.profileImage || '',
        history: data.history || [],
        createdAt: data.createdAt || now,
        updatedAt: now,
        lastModified: now,
        syncStatus: 'synced'
      };

      console.log('Saving admin:', adminToSave);
      const savedAdmin = await saveAdmin(adminToSave);
      
      if (savedAdmin) {
        console.log('Admin saved successfully:', savedAdmin.id);
        
        if (id) {
          await addAdminHistory(id, 'Profile updated', 'Administrator profile information was updated');
        }
        
        toast.success(`Admin ${savedAdmin.firstName} ${savedAdmin.lastName} ${id ? 'updated' : 'created'} successfully!`);
        navigate('/admins');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save admin';
      setSubmitError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin mb-4" />
          <p>Loading admin form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <div>
            <CardTitle className="text-2xl font-bold">
              {id ? 'Edit Administrator' : 'Add New Administrator'}
            </CardTitle>
            <CardDescription>
              {id ? 'Update administrator information' : 'Create a new administrator account'}
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => navigate('/admins')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admins
          </Button>
        </CardHeader>
        
        <CardContent>
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {submitError}
            </div>
          )}
          
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="flex justify-center mb-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={profilePreview} alt="Admin Profile" />
                  <AvatarFallback className="text-lg">
                    {form.getValues('firstName')?.[0] || 'A'}{form.getValues('lastName')?.[0] || 'D'}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <Tabs defaultValue="basic" className="w-full" onValueChange={setSelectedTab}>
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="mt-6">
                  <AdminBasicInfoSection />
                </TabsContent>
                <TabsContent value="contact" className="mt-6">
                  <AdminContactSection />
                </TabsContent>
                <TabsContent value="profile" className="mt-6">
                  <AdminProfileSection 
                    profilePreview={profilePreview} 
                    onProfileImageChange={handleProfileImageChange} 
                  />
                </TabsContent>
                <TabsContent value="qualifications" className="mt-6">
                  <AdminQualificationsSection />
                </TabsContent>
                <TabsContent value="documents" className="mt-6">
                  <AdminDocumentsSection />
                </TabsContent>
                <TabsContent value="permissions" className="mt-6">
                  <AdminPermissionsSection />
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-end pt-6 border-t">
                <Button 
                  type="submit" 
                  disabled={isSaving}
                  className="min-w-[120px]"
                >
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  {id ? 'Update Admin' : 'Create Admin'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminForm;
